import { RoomStageLayout, StreamId } from "@common/domain";
import { createEntityAdapter, EntityAdapter, EntityState } from "@ngrx/entity";
import { Action, createReducer, on } from "@ngrx/store";
import { RoomStageLayoutActions } from "../actions";
import { RoomStageLayoutEvents } from "../events";

export const ROOM_STAGE_LAYOUT_FEATURE_KEY = "roomStageLayout";

export interface RoomStageLayoutState
  extends EntityState<Record<string, never>> {
  isLoading: boolean;
  // Used for optimistic layout update
  nextLayout: null | RoomStageLayout;
  layout: null | RoomStageLayout;
  videos: StreamId[];
  audios: StreamId[];
  focus: null | StreamId;
  main: null | StreamId;
}

export const roomStageLayoutAdapter: EntityAdapter<Record<string, never>> =
  createEntityAdapter();

const initialState: RoomStageLayoutState =
  roomStageLayoutAdapter.getInitialState({
    isLoading: false,
    nextLayout: null,
    layout: null,
    videos: [],
    audios: [],
    focus: null,
    main: null
  });

const reducer = createReducer(
  initialState,
  on(RoomStageLayoutActions.roomStageLayoutFocusDelete, (state) => {
    return {
      ...state,
      isLoading: true,
      focus: null
    };
  }),
  on(
    RoomStageLayoutActions.roomStageLayoutFocusDeleteFailure,
    (_state, { roomStageLayoutState }) => {
      return roomStageLayoutState;
    }
  ),
  on(
    RoomStageLayoutActions.roomStageLayoutFocusSet,
    (
      state,
      {
        roomStageLayoutFocusSetCmd: {
          cmd: { streamId }
        }
      }
    ) => {
      return {
        ...state,
        isLoading: true,
        focus: streamId
      };
    }
  ),
  on(
    RoomStageLayoutActions.roomStageLayoutFocusSetFailure,
    (_state, { roomStageLayoutState }) => {
      return roomStageLayoutState;
    }
  ),
  on(
    RoomStageLayoutActions.roomStageLayoutFocusSetSuccess,
    (state, { roomStageLayoutFocusSetCmd, roomStageLayoutState }) => {
      if (!roomStageLayoutFocusSetCmd) {
        return roomStageLayoutState;
      }

      const { streamId } = roomStageLayoutFocusSetCmd.cmd;

      return {
        ...state,
        focus: streamId,
        isLoading: false
      };
    }
  ),
  on(RoomStageLayoutActions.roomStageLayoutMainDelete, (state) => {
    return {
      ...state,
      isLoading: true,
      main: null
    };
  }),
  on(
    RoomStageLayoutActions.roomStageLayoutMainDeleteFailure,
    (_state, { roomStageLayoutState }) => {
      return roomStageLayoutState;
    }
  ),
  on(
    RoomStageLayoutActions.roomStageLayoutMainSet,
    (
      state,
      {
        roomStageLayoutMainSetCmd: {
          cmd: { streamId }
        }
      }
    ) => {
      return {
        ...state,
        isLoading: true,
        main: streamId
      };
    }
  ),
  on(
    RoomStageLayoutActions.roomStageLayoutMainSetFailure,
    (_state, { roomStageLayoutState }) => {
      return roomStageLayoutState;
    }
  ),
  on(
    RoomStageLayoutActions.roomStageLayoutMainSetSuccess,
    (state, { roomStageLayoutMainSetCmd, roomStageLayoutState }) => {
      if (!roomStageLayoutMainSetCmd) {
        return roomStageLayoutState;
      }

      const { streamId } = roomStageLayoutMainSetCmd.cmd;

      return {
        ...state,
        main: streamId,
        isLoading: false
      };
    }
  ),
  on(
    RoomStageLayoutActions.roomStageLayoutFocusDeleteSuccess,
    RoomStageLayoutActions.roomStageLayoutMainDeleteSuccess,
    (state) => {
      return {
        ...state,
        isLoading: false
      };
    }
  ),
  on(
    RoomStageLayoutEvents.roomStageLayoutStreamsSetEvent,
    (
      state,
      {
        roomStageLayoutStreamsSetEventVO: {
          value: { layout, videos, audios, focus, main }
        }
      }
    ) => {
      // Ignore if next layout does not match the incomming layout
      if (state.nextLayout && state.nextLayout !== layout) {
        return state;
      }

      return {
        ...state,
        nextLayout: null,
        layout,
        videos,
        audios,
        focus: focus ?? null,
        main: main ?? null
      };
    }
  ),
  on(
    RoomStageLayoutActions.roomStageLayoutUpdate,
    (
      state,
      {
        roomStageLayoutUpdateVO: {
          value: { layout }
        }
      }
    ) => ({
      ...state,
      isLoading: true,
      nextLayout: layout
    })
  ),
  on(RoomStageLayoutActions.roomStageLayoutUpdateSuccess, (state) => ({
    ...state,
    isLoading: false
  })),
  on(
    RoomStageLayoutActions.roomStageLayoutUpdateFailure,
    (_state, { roomStageLayoutState }) => roomStageLayoutState
  )
);

export function roomStageLayoutReducer(
  state: RoomStageLayoutState | undefined,
  action: Action
) {
  return reducer(state, action);
}
